import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, DeviceMobile, CircleNotch } from "@phosphor-icons/react";
import { createHandoff } from "@/lib/api";

// "Couch Handoff": generate a 4-digit code + QR that carries the current title & position
// so the viewer can resume on their TV (enter the code at /tv) with no account.
export function SendToTvModal({ videoId, position, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(600);

  useEffect(() => {
    createHandoff(videoId, Math.floor(position || 0))
      .then((d) => {
        setData(d);
        setRemaining(d.expires_in || 600);
      })
      .catch(() => setError("Could not create a code. Please try again."));
  }, [videoId, position]);

  useEffect(() => {
    if (!data) return undefined;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [data]);

  const origin = window.location.origin;
  const deepLink = `${origin}/watch/${videoId}?t=${Math.floor(position || 0)}`;
  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div
      data-testid="send-to-tv-modal"
      className="fixed inset-0 z-[95] grid place-items-center bg-black/70 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0A0A0A] p-8 rv-fade"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="send-to-tv-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors duration-300"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <DeviceMobile size={20} weight="duotone" className="text-amber" />
          <h3 className="font-serif text-2xl text-[#EDEDED]">Send to TV</h3>
        </div>
        <p className="font-mono text-[11px] text-white/40 mb-6 leading-relaxed">
          On your TV, open <span className="text-amber">reelvault.live/tv</span> and enter this code to pick up right where you left off.
        </p>

        {error ? (
          <p data-testid="send-to-tv-error" className="text-crimson text-sm">{error}</p>
        ) : !data ? (
          <div className="flex items-center justify-center py-10 text-white/40">
            <CircleNotch size={28} className="animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-5">
            <div
              data-testid="handoff-code"
              className="font-mono text-5xl tracking-[0.4em] text-amber pl-[0.4em] tabular-nums"
            >
              {data.code}
            </div>
            <div className="rounded-xl bg-white p-3">
              <QRCodeSVG value={deepLink} size={148} level="M" />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">
              Or scan to open on another device · expires in {mm}:{ss}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
